import { observer } from 'mobx-react-lite'
import { FilterControlsContainer } from '@/components/FilterControls/FilterControlsContainer'
import { useToggle } from '@/hooks/state-hooks'
import { VirtualTableContainer } from '@/components/VirtualTableContainer'
import { type ProPayoutsViewModel } from '@/features/payouts/pro/ProPayoutsViewModel'
import { type ProHistoricalTransactionViewModel } from '@/features/payouts/pro/ProHistoricalTransactionViewModel'
import {
  ProHistoricalTransactionRow,
  ProHistoricalTransactionsTable,
  ProHistoricalTransactionsTableHeader,
} from '@/features/payouts/pro/components/ProHistoricalTransactionsTable'

export interface ProHistoricalTransactionsTableContainerProps {
  vm: ProPayoutsViewModel
}

export const ProHistoricalTransactionsTableContainer = observer(
  function ProHistoricalTransactionsTableContainer({
    vm,
  }: ProHistoricalTransactionsTableContainerProps) {
    const filtersShowing = useToggle(false)

    return (
      <ProHistoricalTransactionsTable
        searchText={vm.searchText.value}
        onSearchTextChange={vm.searchText.change}
        filtersShowing={filtersShowing.isEnabled}
        onToggleFilters={filtersShowing.toggle}
        appliedFilterCount={vm.filters.appliedFilterCount}
        isExportPending={vm.exportCsv.pending}
        onExportCsv={vm.exportCsv}
        filterControls={
          <FilterControlsContainer
            vm={vm.filters}
            active={filtersShowing.isEnabled}
            editorTestId="job"
            testId="job-filter"
          />
        }
        showTable={vm.showTable}
        error={vm.transactionsScroller.fetch.error}
        appliedFilters={vm.filtersApplied}
        onClearFilters={vm.clearFilters}
      >
        <VirtualTableContainer
          data={vm.transactions}
          overscan={400}
          scroller={vm.transactionsScroller}
          itemContent={(_index, transactionVm) => (
            <ProHistoricalTransactionRowContainer vm={transactionVm} />
          )}
          headerContent={() => <ProHistoricalTransactionsTableHeader />}
        />
      </ProHistoricalTransactionsTable>
    )
  },
)

interface ProHistoricalTransactionRowContainerProps {
  vm: ProHistoricalTransactionViewModel
}

const ProHistoricalTransactionRowContainer = observer(
  function ProHistoricalTransactionRowContainer({
    vm,
  }: ProHistoricalTransactionRowContainerProps) {
    const transaction = vm.transaction
    return (
      <ProHistoricalTransactionRow
        id={transaction.id}
        completedAt={transaction.completedAt}
        workspaceName={transaction.workspace.name}
        workspaceIcon={transaction.workspace.branding.logos.icon}
        source={transaction.source}
        memo={transaction.memo}
        reason={transaction.reason}
        jobShortId={transaction.job?.shortId}
        jobDisplayName={transaction.job?.displayName}
        hasJob={!!transaction.job}
        amount={transaction.amount}
        onViewJob={vm.viewJob}
      />
    )
  },
)
